/**
 * B2BInventoryBalancesPage — מלאי
 *
 * Mobile-first inventory balances view for B2B agents.
 * Loads the warehouse list first via `b2b_agent_warehouses`, then fetches
 * balances per-warehouse via `b2b_agent_inventory_balances` with p_warehouse_id.
 * This avoids the PostgREST 1000-row limit that caused silent truncation for
 * master agents who see all warehouses.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PackageOpen,
  Search,
  RefreshCw,
  Loader as Loader2,
  CircleAlert as AlertCircle,
  Download,
  FileSpreadsheet,
  Printer,
  Warehouse,
  Filter,
  Package,
  ChevronDown,
  X,
  Send,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useB2BUser } from './B2BUserContext';
import { SendConsignmentReportModal } from '@/components/consignment/SendConsignmentReportModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InventoryBalance {
  product_id: string;
  sku: string | null;
  product_name: string;
  image_url: string | null;
  warehouse_id: string;
  warehouse_name: string;
  calculated_quantity: number;
  total_value: number;
}

interface WarehouseOption {
  id: string;
  name: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(n: number): string {
  return n.toLocaleString('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatNumber(n: number): string {
  if (Number.isInteger(n)) return n.toLocaleString('he-IL');
  return parseFloat(n.toFixed(3)).toLocaleString('he-IL');
}

// ─── Export helpers ───────────────────────────────────────────────────────────

function exportToExcel(rows: InventoryBalance[]) {
  const BOM = '\uFEFF';
  const headers = ['מקט', 'שם מוצר', 'מחסן', 'כמות', 'ערך (₪)'];
  const csvRows = [
    headers.join(','),
    ...rows.map((r) =>
      [
        r.sku ?? '',
        `"${(r.product_name ?? '').replace(/"/g, '""')}"`,
        `"${(r.warehouse_name ?? '').replace(/"/g, '""')}"`,
        r.calculated_quantity,
        r.total_value.toFixed(2),
      ].join(',')
    ),
  ];
  const csv = BOM + csvRows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `inventory-balances-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function exportToPDF(tableEl: HTMLElement | null) {
  if (!tableEl) return;
  tableEl.style.display = 'block';
  try {
    const { exportTableToPDF } = await import('../../lib/pdfExport');
    await exportTableToPDF(tableEl, `inventory_balances_${new Date().toISOString().slice(0, 10)}.pdf`);
  } finally {
    tableEl.style.display = '';
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function B2BInventoryBalancesPage() {
  const navigate = useNavigate();
  const { session } = useB2BUser();

  const companyId = session?.activeCompanyId ?? null;
  const isAgent = session?.role === 'agent';
  const activeClient = session?.activeClientRecord ?? null;
  const clientWarehouseId = activeClient?.consignment_warehouse_id ?? null;
  const vanWarehouseId = session?.agentRecord?.default_warehouse_id ?? null;

  // ── State ────────────────────────────────────────────────────────────────────
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [warehousesLoading, setWarehousesLoading] = useState(true);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [data, setData] = useState<InventoryBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const printTableRef = useRef<HTMLDivElement>(null);
  const canSendReport =
    !!activeClient && !!clientWarehouseId &&
    (activeClient.is_own_client !== false || !!session?.agentRecord?.is_master_agent);

  // ── Auth guard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (session && !isAgent) {
      navigate('/b2b', { replace: true });
    }
  }, [session, isAgent, navigate]);

  // ── Step 1: Fetch warehouse list (only when no client is active) ───────────
  const fetchWarehouses = useCallback(async () => {
    if (!companyId) return;
    if (activeClient) {
      setWarehousesLoading(false);
      return;
    }
    setWarehousesLoading(true);
    const { data: result, error: rpcError } = await supabase.rpc(
      'b2b_agent_warehouses',
      { p_company_id: companyId }
    );
    setWarehousesLoading(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    const whs: WarehouseOption[] = (result ?? []).map((r: { warehouse_id: string; warehouse_name: string }) => ({
      id: r.warehouse_id,
      name: r.warehouse_name,
    }));
    setWarehouses(whs);
    const first = whs[0];
    if (first && !selectedWarehouse) {
      setSelectedWarehouse(first.id);
    }
  }, [companyId, selectedWarehouse, activeClient]);

  // ── When a client is active, auto-select their warehouse ───────────────────
  type WarehouseView = 'client' | 'van';
  const [warehouseView, setWarehouseView] = useState<WarehouseView>('client');

  useEffect(() => {
    if (activeClient && clientWarehouseId) {
      setSelectedWarehouse(clientWarehouseId);
      setWarehouseView('client');
      setWarehousesLoading(false);
    }
  }, [activeClient, clientWarehouseId]);

  const handleToggleView = useCallback((view: WarehouseView) => {
    setWarehouseView(view);
    if (view === 'client' && clientWarehouseId) {
      setSelectedWarehouse(clientWarehouseId);
    } else if (view === 'van' && vanWarehouseId) {
      setSelectedWarehouse(vanWarehouseId);
    }
  }, [clientWarehouseId, vanWarehouseId]);

  useEffect(() => {
    fetchWarehouses();
  }, [fetchWarehouses]);

  // ── Step 2: Fetch balances for selected warehouse ───────────────────────────
  const fetchData = useCallback(async () => {
    if (!companyId || !selectedWarehouse) return;
    setLoading(true);
    setError(null);
    const { data: result, error: rpcError } = await supabase.rpc(
      'b2b_agent_inventory_balances',
      {
        p_company_id: companyId,
        p_warehouse_id: selectedWarehouse,
      }
    );
    setLoading(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    setData((result as InventoryBalance[]) ?? []);
  }, [companyId, selectedWarehouse]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Refresh both warehouse list and data ────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    await fetchWarehouses();
    await fetchData();
  }, [fetchWarehouses, fetchData]);

  // ── Derived: filtered by search text ────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter(
      (r) =>
        r.product_name.toLowerCase().includes(q) ||
        (r.sku && r.sku.toLowerCase().includes(q))
    );
  }, [data, search]);

  // ── Derived: summary ─────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const totalProducts = new Set(filtered.map((r) => r.product_id)).size;
    const totalUnits = filtered.reduce((s, r) => s + r.calculated_quantity, 0);
    const totalValue = filtered.reduce((s, r) => s + r.total_value, 0);
    return { totalProducts, totalUnits, totalValue };
  }, [filtered]);

  // ── Early returns ────────────────────────────────────────────────────────────
  if (!session) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
      </div>
    );
  }

  if (!isAgent) return null;

  const isInitialLoading = warehousesLoading || (loading && data.length === 0);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4 print:max-w-none print:px-0" dir="rtl">
      {/* ── Header ────────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2 print:hidden">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Package className="w-5 h-5 text-primary-600" />
          מלאי
        </h1>

        <div className="flex items-center gap-2">
          {canSendReport && (
            <button
              onClick={() => setSendOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
                         bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              שלח יתרות לחנות
            </button>
          )}
          {/* Export button */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border
                         bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              ייצוא
              <ChevronDown className="w-3 h-3" />
            </button>

            {showExportMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowExportMenu(false)}
                />
                <div className="absolute left-0 top-full mt-1 z-20 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[140px]">
                  <button
                    onClick={() => {
                      exportToExcel(filtered);
                      setShowExportMenu(false);
                    }}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                    Excel (CSV)
                  </button>
                  <button
                    disabled={pdfBusy}
                    onClick={async () => {
                      setShowExportMenu(false);
                      setPdfBusy(true);
                      try { await exportToPDF(printTableRef.current); } finally { setPdfBusy(false); }
                    }}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <Printer className="w-4 h-4 text-blue-600" />}
                    {pdfBusy ? 'מייצר PDF…' : 'PDF (הורדה)'}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={loading || warehousesLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border
                       bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 transition-colors
                       disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            רענן
          </button>
        </div>
      </div>

      {/* ── Print-only header ──────────────────────────────────────────────────── */}
      <div className="hidden print:block print:mb-6">
        <h1 className="text-2xl font-bold text-gray-900 text-center">דו״ח מלאי</h1>
        <p className="text-sm text-gray-500 text-center mt-1">
          {new Date().toLocaleDateString('he-IL')}
          {selectedWarehouse &&
            ` • ${warehouses.find((w) => w.id === selectedWarehouse)?.name ?? ''}`}
          {search.trim() && ` • חיפוש: "${search.trim()}"`}
        </p>
      </div>

      {/* ── Search + Warehouse filter ──────────────────────────────────────────── */}
      <div className="space-y-3 print:hidden">
        {/* Search */}
        <div className="relative">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="חיפוש לפי שם מוצר או מקט…"
            className="w-full pr-9 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm
                       focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 bg-gray-50"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Warehouse toggle (when a client is active) */}
        {activeClient && clientWarehouseId && (
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => handleToggleView('client')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all
                ${warehouseView === 'client'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Warehouse className="w-3.5 h-3.5" />
              מחסן לקוח
            </button>
            <button
              onClick={() => handleToggleView('van')}
              disabled={!vanWarehouseId}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg transition-all
                disabled:opacity-40 disabled:cursor-not-allowed
                ${warehouseView === 'van'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Package className="w-3.5 h-3.5" />
              הרכב שלי
            </button>
          </div>
        )}

        {/* Warehouse dropdown (only when NO client is active) */}
        {!activeClient && warehouses.length > 0 && (
          <div className="relative">
            <Warehouse className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedWarehouse}
              onChange={(e) => setSelectedWarehouse(e.target.value)}
              className="w-full pr-9 pl-4 py-2.5 border border-gray-200 rounded-xl text-sm appearance-none
                         focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 bg-gray-50"
            >
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* ── Summary bar ────────────────────────────────────────────────────────── */}
      {!isInitialLoading && !error && filtered.length > 0 && (
        <div className="grid grid-cols-3 gap-3 print:gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-center">
            <p className="text-[11px] text-gray-500 font-medium">מוצרים</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">
              {summary.totalProducts.toLocaleString('he-IL')}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-center">
            <p className="text-[11px] text-gray-500 font-medium">יחידות</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">
              {formatNumber(summary.totalUnits)}
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 text-center">
            <p className="text-[11px] text-gray-500 font-medium">ערך כולל</p>
            <p className="text-lg font-bold text-primary-700 mt-0.5">
              {formatCurrency(summary.totalValue)}
            </p>
          </div>
        </div>
      )}

      {/* ── Loading state ──────────────────────────────────────────────────────── */}
      {isInitialLoading && (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
          <p className="text-sm">טוען מלאי…</p>
        </div>
      )}

      {/* Subtle inline loader when switching warehouses (not initial load) */}
      {loading && !isInitialLoading && (
        <div className="flex items-center justify-center py-3 gap-2 text-gray-400 text-xs">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>טוען נתוני מחסן…</span>
        </div>
      )}

      {/* ── Error state ────────────────────────────────────────────────────────── */}
      {!loading && error && (
        <div className="flex flex-col items-center gap-4 py-16">
          <div className="flex items-start gap-3 px-4 py-3.5 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700 w-full max-w-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">שגיאה בטעינת המלאי</p>
              <p className="text-xs mt-0.5 text-red-500">{error}</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            נסה שנית
          </button>
        </div>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────────── */}
      {!isInitialLoading && !loading && !error && data.length === 0 && selectedWarehouse && (
        <div className="flex flex-col items-center justify-center py-24 gap-5">
          <div className="flex items-center justify-center w-20 h-20 rounded-3xl bg-gray-100">
            <PackageOpen className="w-10 h-10 text-gray-300" />
          </div>
          <div className="text-center space-y-1.5">
            <h2 className="text-lg font-bold text-gray-800">אין נתוני מלאי</h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              לא נמצאו יתרות מלאי במחסן זה.
            </p>
          </div>
        </div>
      )}

      {/* ── No warehouses state ────────────────────────────────────────────────── */}
      {!warehousesLoading && !error && warehouses.length === 0 && !activeClient && (
        <div className="flex flex-col items-center justify-center py-24 gap-5">
          <div className="flex items-center justify-center w-20 h-20 rounded-3xl bg-gray-100">
            <Warehouse className="w-10 h-10 text-gray-300" />
          </div>
          <div className="text-center space-y-1.5">
            <h2 className="text-lg font-bold text-gray-800">לא נמצאו מחסנים</h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              לא נמצאו מחסנים המשויכים אליך.
            </p>
          </div>
        </div>
      )}

      {/* ── No results for search ─────────────────────────────────────────────── */}
      {!isInitialLoading && !loading && !error && data.length > 0 && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-3xl bg-gray-100">
            <Filter className="w-8 h-8 text-gray-300" />
          </div>
          <div className="text-center space-y-1.5">
            <h2 className="text-base font-bold text-gray-700">לא נמצאו תוצאות</h2>
            <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
              נסה לשנות את החיפוש.
            </p>
          </div>
          <button
            onClick={() => setSearch('')}
            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            <X className="w-4 h-4" />
            נקה חיפוש
          </button>
        </div>
      )}

      {/* ── Product cards ──────────────────────────────────────────────────────── */}
      {!isInitialLoading && !error && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((item) => (
            <div
              key={`${item.product_id}-${item.warehouse_id}`}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden
                         transition-shadow hover:shadow-md print:shadow-none print:border-gray-200"
            >
              <div className="flex items-center gap-3 p-4">
                {/* Thumbnail */}
                <div className="w-14 h-14 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center shrink-0 overflow-hidden">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <PackageOpen className="w-6 h-6 text-gray-300" />
                  )}
                </div>

                {/* Product info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900 truncate">
                    {item.product_name}
                  </h3>
                  {item.sku && (
                    <p className="text-xs text-gray-500 mt-0.5 font-mono">{item.sku}</p>
                  )}
                  {/* Warehouse badge */}
                  <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold
                                   bg-blue-50 text-blue-700 border border-blue-100">
                    <Warehouse className="w-2.5 h-2.5" />
                    {item.warehouse_name}
                  </span>
                </div>

                {/* Quantity + Value */}
                <div className="text-left shrink-0">
                  <p className="text-xl font-bold text-gray-900 leading-tight">
                    {formatNumber(item.calculated_quantity)}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">יחידות</p>
                  <p className="text-sm font-semibold text-primary-600 mt-1">
                    {formatCurrency(item.total_value)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Print table (hidden on screen, used for PDF export) ────────────────── */}
      {!loading && !error && filtered.length > 0 && (
        <div ref={printTableRef} className="hidden print:block mt-6" dir="rtl">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="py-2 text-right font-bold text-gray-700">מקט</th>
                <th className="py-2 text-right font-bold text-gray-700">שם מוצר</th>
                <th className="py-2 text-right font-bold text-gray-700">מחסן</th>
                <th className="py-2 text-left font-bold text-gray-700">כמות</th>
                <th className="py-2 text-left font-bold text-gray-700">ערך</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr
                  key={`print-${item.product_id}-${item.warehouse_id}`}
                  className="border-b border-gray-200"
                >
                  <td className="py-1.5 text-right font-mono text-xs">{item.sku ?? '—'}</td>
                  <td className="py-1.5 text-right">{item.product_name}</td>
                  <td className="py-1.5 text-right">{item.warehouse_name}</td>
                  <td className="py-1.5 text-left font-semibold">
                    {formatNumber(item.calculated_quantity)}
                  </td>
                  <td className="py-1.5 text-left">{formatCurrency(item.total_value)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-400 font-bold">
                <td className="py-2" colSpan={3}>
                  סה״כ ({summary.totalProducts} מוצרים)
                </td>
                <td className="py-2 text-left">{formatNumber(summary.totalUnits)}</td>
                <td className="py-2 text-left">{formatCurrency(summary.totalValue)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── Bottom results count ───────────────────────────────────────────────── */}
      {!isInitialLoading && !loading && !error && filtered.length > 0 && (
        <p className="text-center text-xs text-gray-400 pb-2 print:hidden">
          מציג {filtered.length} שורות מלאי
        </p>
      )}

      {sendOpen && activeClient && (
        <SendConsignmentReportModal
          companyId={activeClient.company_id}
          clientId={activeClient.id}
          clientName={activeClient.name}
          onClose={() => setSendOpen(false)}
        />
      )}
    </div>
  );
}
